import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import AsyncStorage from "@react-native-async-storage/async-storage"
import type { StudentUser } from "students-api"
import { BaseAuthState } from "toptaal-shared"

interface AuthState extends BaseAuthState {
  user: StudentUser | null
  isAuthenticated: boolean

  onboardingData: {
    bsnRequired?: boolean
    needsOnboarding?: boolean
    studentProjectOrganizationId?: string
    hasLessonAvailability?: boolean
  }

  setBsnRequired: ({ bsnRequired }: { bsnRequired: boolean }) => void
  setNeedsOnboarding: ({
    needsOnboarding,
  }: {
    needsOnboarding: boolean
  }) => void

  setStudentProjectOrganizationId: ({
    studentProjectOrganizationId,
  }: {
    studentProjectOrganizationId: string
  }) => void

  setHasLessonAvailability: ({
    hasLessonAvailability,
  }: {
    hasLessonAvailability: boolean
  }) => void

  getBsnRequired: () => boolean | undefined
  getNeedsOnboarding: () => boolean | undefined
  getStudentProjectOrganizationId: () => string | undefined
  getHasLessonAvailability: () => boolean | undefined

  setUser: (user: StudentUser) => void
  clearUser: () => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      onboardingData: {
        bsnRequired: undefined,
        needsOnboarding: undefined,
      },

      setNeedsOnboarding: ({ needsOnboarding }) =>
        set((state) => ({
          onboardingData: {
            ...state.onboardingData,
            needsOnboarding,
          },
        })),
      setBsnRequired: ({ bsnRequired }) =>
        set((state) => ({
          onboardingData: {
            ...state.onboardingData,
            bsnRequired,
          },
        })),

      setStudentProjectOrganizationId: ({ studentProjectOrganizationId }) =>
        set((state) => ({
          onboardingData: {
            ...state.onboardingData,
            studentProjectOrganizationId,
          },
        })),

      setHasLessonAvailability: ({ hasLessonAvailability }) =>
        set((state) => ({
          onboardingData: {
            ...state.onboardingData,
            hasLessonAvailability,
          },
        })),

      getStudentProjectOrganizationId: () =>
        get().onboardingData.studentProjectOrganizationId,

      getHasLessonAvailability: () =>
        get().onboardingData.hasLessonAvailability,

      getNeedsOnboarding: () => get().onboardingData.needsOnboarding,
      getBsnRequired: () => get().onboardingData.bsnRequired,

      setToken: (token) => set({ token, isAuthenticated: true }),
      setRefreshToken: (refreshToken) => set({ refreshToken }),
      clearTokens: () =>
        set({ token: null, refreshToken: null, isAuthenticated: false }),
      getToken: () => get().token,
      getRefreshToken: () => get().refreshToken,
      setUser: (user) => set({ user }),
      clearUser: () => set({ user: null }),
      logout: () =>
        set({
          onboardingData: {},
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
)
